import { DEFAULT_PAGE_SIZE } from '@/constants';
import { usePaginatedCrudModel } from '@/models/shared/usePaginatedCrudModel';
import {
  createBeneficiary,
  deleteBeneficiary,
  downloadBeneficiaries,
  fetchBeneficiaries,
  reactivateBeneficiary,
  updateBeneficiary,
  uploadBeneficiariesFile,
} from '@/services/beneficiary/beneficiaryService';
import type {
  Beneficiary,
  BeneficiaryCreateRequest,
  BeneficiaryListParams,
  BeneficiaryListResult,
  BeneficiaryUpdateRequest,
} from '@/types/beneficiary';
import { useCallback } from 'react';

const useBeneficiaryModel = () => {
  const {
    items: beneficiaries,
    loading,
    pagination,
    loadItems: loadBeneficiaries,
    create,
    update: updateById,
    remove,
  } = usePaginatedCrudModel<
    Beneficiary,
    BeneficiaryListParams,
    BeneficiaryCreateRequest,
    BeneficiaryUpdateRequest,
    BeneficiaryListResult
  >({
    defaultPageSize: DEFAULT_PAGE_SIZE,
    initialQuery: {},
    loadList: fetchBeneficiaries,
    buildQuery: (params, lastQuery, paginationState) => {
      const text =
        'text' in params
          ? params.text
          : 'search' in params
          ? params.search
          : 'text' in lastQuery
          ? lastQuery.text
          : lastQuery.search;

      return {
        page: params.page ?? paginationState.current,
        size:
          params.size ??
          params.page_size ??
          params.pageSize ??
          paginationState.pageSize,
        text,
        status: 'status' in params ? params.status : lastQuery.status,
        segment: 'segment' in params ? params.segment : lastQuery.segment,
      };
    },
    createItem: createBeneficiary,
    updateItem: async (_id, payload) => updateBeneficiary(payload),
    deleteItem: deleteBeneficiary,
  });

  // Preserve existing public API: update(payload) instead of update(id, payload)
  const update = useCallback(
    async (payload: BeneficiaryUpdateRequest) => {
      return updateById(payload.id, payload);
    },
    [updateById],
  );

  const exportBeneficiaries = useCallback(async () => {
    return downloadBeneficiaries();
  }, []);

  const upload = useCallback(async (file: File) => {
    return uploadBeneficiariesFile(file);
  }, []);

  const reactivate = useCallback(async (id: number) => {
    return reactivateBeneficiary(id);
  }, []);

  return {
    beneficiaries,
    loading,
    pagination,
    loadBeneficiaries,
    create,
    update,
    remove,
    reactivate,
    exportBeneficiaries,
    upload,
  };
};

export default useBeneficiaryModel;
